// react
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box } from '@mui/material';
import { AutoAwesome as IconAI } from '@mui/icons-material';

// components
import { AiChat, type ChatMessage, type LlmHistoryMessage } from '../../../ui/AiChat/AiChat';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// context
import { ContextProject } from '../../Project/Project.context';

// services
import { sendRagChat } from '../../../../services/aiApi';
import type { LlmMessage } from '../../../../services/aiApi';

/**
 * In-memory cache of chat history, keyed by project id.
 * Lives for the lifetime of the SPA (cleared on full reload), so the user's
 * conversation survives navigating to other tabs and back while the app is open.
 */
const chatHistoryCache = new Map<number, ChatMessage[]>();

/** Project ids with a request currently in flight. */
const inflightProjects = new Set<number>();

/** Subscribers notified whenever the cache or in-flight set changes. */
const cacheListeners = new Set<() => void>();
const notifyCacheChange = () => {
	cacheListeners.forEach((fn) => {
		try {
			fn();
		} catch {
			/* listener errors must not break others */
		}
	});
};
const subscribeCacheChange = (fn: () => void) => {
	cacheListeners.add(fn);
	return () => {
		cacheListeners.delete(fn);
	};
};

/**
 * Chat page — AI chat interface backed by the agentic RAG endpoint.
 * The agent can search and list project documents autonomously.
 * Accessible at /project/:id/spend-plans/chat
 */
export const Chat: React.FC = () => {
	const { t } = useTranslation();
	const { project } = useContext(ContextProject);
	const abortControllerRef = useRef<AbortController | null>(null);
	const [chatKey, setChatKey] = useState(0);

	const handleSend = useCallback(
		async ({
			message,
			history,
		}: {
			message: string;
			history: LlmHistoryMessage[];
			onChunk?: (chunk: string) => void;
		}): Promise<{ response: string; meta?: { model?: string; duration_ms?: number; service?: string } }> => {
			if (!project?.id) {
				return { response: 'Project context is not loaded yet. Please try again in a moment.' };
			}

			// Create a new AbortController for this request
			const controller = new AbortController();
			abortControllerRef.current = controller;

			// Mark this project as in-flight so a remounted AiChat shows typing dots
			inflightProjects.add(project.id);
			notifyCacheChange();

			const messages: LlmMessage[] = [
				...(history as LlmMessage[]),
				{ role: 'user', content: message },
			];

			try {
				const result = await sendRagChat(
					{
						project_id: project.id,
						messages,
					},
					controller.signal
				);

				const meta = {
					model: result.model,
					duration_ms: result.duration_ms,
					service: result.service,
				};

				// Mirror the final assistant turn into the cache directly. This way,
				// if the user navigated away mid-request and AiChat is unmounted, the
				// answer is still captured and shown when they come back.
				if (project?.id) {
					const cached = chatHistoryCache.get(project.id) ?? [];
					const userMsg: ChatMessage = {
						id: `msg-bg-user-${Date.now()}`,
						role: 'user',
						content: message,
						timestamp: new Date(),
					};
					const assistantMsg: ChatMessage = {
						id: `msg-bg-asst-${Date.now()}`,
						role: 'assistant',
						content: result.reply,
						timestamp: new Date(),
						meta,
					};
					// If the cached tail already contains this user turn (mounted path,
					// AiChat already pushed it), only append the assistant. Otherwise
					// (unmounted path — placeholder was filtered out) append both.
					const tail = cached[cached.length - 1];
					const userAlreadyCached =
						tail && tail.role === 'user' && tail.content === message;
					chatHistoryCache.set(
						project.id,
						userAlreadyCached
							? [...cached, assistantMsg]
							: [...cached, userMsg, assistantMsg]
					);
					notifyCacheChange();
				}

				return {
					response: result.reply,
					meta,
				};
			} finally {
				abortControllerRef.current = null;
				inflightProjects.delete(project.id);
				notifyCacheChange();
			}
		},
		[project]
	);

	const handleStop = useCallback(() => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
	}, []);

	const handleNewChat = useCallback(() => {
		// Abort any in-flight request
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		// Drop the cached history for this project
		if (project?.id) {
			chatHistoryCache.delete(project.id);
		}
		// Increment key to fully reset AiChat internal state
		setChatKey((k) => k + 1);
	}, [project?.id]);

	// Persist messages per project so they survive tab navigation.
	// Strip a trailing empty assistant placeholder — that bubble only exists
	// while a request is in flight in the live component, and we don't want to
	// re-seed an empty bubble if the user navigates away and comes back.
	const handleMessagesChange = useCallback(
		(messages: ChatMessage[]) => {
			if (!project?.id) return;
			const last = messages[messages.length - 1];
			const persistable =
				last && last.role === 'assistant' && !last.content
					? messages.slice(0, -1)
					: messages;
			chatHistoryCache.set(project.id, persistable);
		},
		[project?.id]
	);

	const initialMessages = project?.id ? chatHistoryCache.get(project.id) : undefined;

	// Re-render whenever the module-level cache or in-flight set changes, so a
	// remounted (or still-mounted) AiChat picks up answers / typing state from
	// background requests started in another tab.
	const [, forceTick] = useState(0);
	useEffect(() => {
		const unsubscribe = subscribeCacheChange(() => forceTick((t) => t + 1));
		return unsubscribe;
	}, []);

	const isGenerating = project?.id ? inflightProjects.has(project.id) : false;
	const syncMessages = project?.id ? chatHistoryCache.get(project.id) : undefined;

	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<HeaderListingPage
				icon={IconAI}
				title="AI Chat"
					subtitle={t('spendPlan.aiChat.subtitle')}
				variant="activity"
			/>

			{/* Chat area */}
			<Box sx={{ flex: 1, overflow: 'hidden' }}>
				<AiChat
					key={chatKey}
					onSend={handleSend}
					onStop={handleStop}
					onNewChat={handleNewChat}
					initialMessages={initialMessages}
					onMessagesChange={handleMessagesChange}
					syncMessages={syncMessages}
					externalIsLoading={isGenerating}
					placeholder="Ask me anything about this project…"
					welcomeMessage={
						project?.name
							? `Hello! I'm the BLDT AI assistant. Ask me anything about ${project.name} — contracts, spend plans, payments, or documents.`
							: "Hello! I'm the BLDT AI assistant. Ask me anything about your project data, contracts, or spend plans."
					}
					maxHeight="calc(100vh - 200px)"
				/>
			</Box>
		</Box>
	);
};

